import React, { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  Text,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  Buildings,
  CurrencyDollar,
  Receipt,
  WarningCircle,
  UsersThree,
  ArrowsLeftRight,
  MagnifyingGlass,
  Files,
  Note,
} from 'phosphor-react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { num, pct, qtd } from '../format';
import {
  IEscritorioKpis,
  IEscritorioRankingEmpresa,
  IEscritorioRankingColaborador,
} from '../types';
import { KpiCard } from '../components/KpiCard';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { EmptyState } from '../components/EmptyState';
import { useAuth } from '../../../../providers/auth';
import { UI } from '../ui';

interface EscritorioScreenProps {
  periodo: IPeriodoParams;
  /** Drill: clicar numa empresa do ranking abre o dashboard dela. */
  onOpenEmpresa: (id: number) => void;
  /** Drill: abrir a empresa num submódulo específico (ex.: Dossiê 360). */
  onOpenEmpresaScreen: (id: number, screen: string) => void;
}

const riscoColor = (score: number): string => {
  if (score < 60) return UI.neg;
  if (score < 80) return UI.warn;
  return UI.pos;
};

const Glass = ({
  title,
  sub,
  action,
  children,
}: {
  title: string;
  sub?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box
    borderRadius="18px"
    bg="rgba(255,255,255,0.7)"
    backdropFilter="blur(14px) saturate(160%)"
    border="1px solid rgba(255,255,255,0.85)"
    boxShadow="0 12px 34px -18px rgba(15,23,42,0.35)"
    overflow="hidden"
  >
    <Flex justify="space-between" align="center" px={5} pt={4} pb={3} gap={3} wrap="wrap">
      <Box>
        <Text fontSize="13px" fontWeight={800} color={UI.ink}>
          {title}
        </Text>
        {sub && (
          <Text fontSize="11px" color={UI.faint} fontWeight={600}>
            {sub}
          </Text>
        )}
      </Box>
      {action}
    </Flex>
    {children}
  </Box>
);

const CoberturaBar = ({ label, valor, total }: { label: string; valor: number; total: number }) => {
  const p = total ? (valor / total) * 100 : 0;
  return (
    <Flex align="center" gap={3} mb={2.5}>
      <Text w="64px" fontSize="12px" fontWeight={700} color={UI.muted} flexShrink={0}>
        {label}
      </Text>
      <Box flex={1} h="9px" borderRadius="full" bg="rgba(15,23,42,0.06)" overflow="hidden">
        <Box
          h="100%"
          borderRadius="full"
          bgGradient={`linear(to-r, ${UI.brand}, #1AA6B7)`}
          w={`${p}%`}
        />
      </Box>
      <Text
        w="92px"
        textAlign="right"
        fontSize="12px"
        fontWeight={700}
        color={UI.ink}
        flexShrink={0}
      >
        {qtd(valor)}/{qtd(total)} · {pct(p)}
      </Text>
    </Flex>
  );
};

export function EscritorioScreen({
  periodo,
  onOpenEmpresa,
  onOpenEmpresaScreen,
}: EscritorioScreenProps) {
  const { getEscritorioKpis, getEscritorioRankingEmpresas, getEscritorioRankingColaboradores } =
    useAuditoriaInteligente();
  const { temVisaoTodosGrupos } = useAuth();
  const visaoCarteira = temVisaoTodosGrupos();
  const [buscaEmp, setBuscaEmp] = useState('');
  const [buscaCol, setBuscaCol] = useState('');

  const {
    data: kpis,
    isFetching,
    isError,
  } = useQuery<IEscritorioKpis>({
    queryKey: ['ai-escritorio-kpis', { ...periodo }],
    queryFn: getEscritorioKpis,
    enabled: visaoCarteira,
  });

  const { data: empresas } = useQuery<IEscritorioRankingEmpresa[]>({
    queryKey: ['ai-escritorio-ranking-empresas', { ...periodo }],
    queryFn: getEscritorioRankingEmpresas,
    enabled: visaoCarteira,
  });

  const { data: colaboradores } = useQuery<IEscritorioRankingColaborador[]>({
    queryKey: ['ai-escritorio-ranking-colaboradores', { ...periodo }],
    queryFn: getEscritorioRankingColaboradores,
    enabled: visaoCarteira,
  });

  const classificacoes = useMemo(() => {
    const map = new Map<string, number>();
    (empresas ?? []).forEach(e => map.set(e.classificacao, (map.get(e.classificacao) ?? 0) + 1));
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [empresas]);
  const maxClass = Math.max(...classificacoes.map(c => c[1]), 1);

  const empFiltradas = useMemo(() => {
    const q = buscaEmp.trim().toLowerCase();
    if (!q) return empresas ?? [];
    return (empresas ?? []).filter(e =>
      `${e.nome} ${e.cnpj} ${e.external_id}`.toLowerCase().includes(q),
    );
  }, [empresas, buscaEmp]);
  const colFiltrados = useMemo(() => {
    const q = buscaCol.trim().toLowerCase();
    if (!q) return colaboradores ?? [];
    return (colaboradores ?? []).filter(c => c.usuario.toLowerCase().includes(q));
  }, [colaboradores, buscaCol]);

  if (!visaoCarteira) {
    return (
      <EmptyState
        title="Acesso restrito"
        message="A Gestão do Escritório é uma visão gerencial de toda a carteira, disponível apenas para perfis com acesso a todos os grupos de empresas."
        icon={Buildings}
      />
    );
  }

  if (isError) {
    return (
      <EmptyState
        title="Não foi possível carregar"
        message="Ocorreu um erro ao buscar os dados do escritório. Atualize a página e tente novamente."
        icon={WarningCircle}
      />
    );
  }

  const total = num(kpis?.total_empresas);

  return (
    <Box>
      <Grid
        templateColumns={{ base: '1fr 1fr', md: 'repeat(3, 1fr)', xl: 'repeat(6, 1fr)' }}
        gap={4}
        mb={5}
      >
        <KpiCard
          label="Empresas"
          value={qtd(kpis?.total_empresas ?? 0)}
          icon={Buildings}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Quantidade de empresas ativas na carteira do escritório. Conta apenas clientes em atividade, sem os excluídos."
              dados="tb_empresa (status ativa = 'Ativa', sem exclusão lógica)"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Faturamento"
          value={formatCurrency(kpis?.faturamento_total ?? 0)}
          icon={CurrencyDollar}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Faturamento de toda a carteira no período, na regra do Relatório de Faturamento do Domínio: acumuladores com IFAT = S (saídas − devoluções + serviços)."
              dados="extract_acumuladores.saldo (ifat = 'S', mensal) — mês sem extração v2 usa as notas de venda + NFS-e"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Impostos"
          value={formatCurrency(kpis?.impostos_total ?? 0)}
          icon={Receipt}
          iconColor={UI.warn}
          iconBg={UI.warnSoft}
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Total de impostos apurados na carteira no período (DAS, ICMS, PIS, COFINS e demais tributos consolidados)."
              dados="extract_impostos.valor_total (somado por período)"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Folha"
          value={formatCurrency(kpis?.folha_total ?? 0)}
          icon={UsersThree}
          iconColor="#8B5CF6"
          iconBg="#F0EAFB"
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Total de proventos da folha de pagamento de toda a carteira nas competências do período."
              dados="extract_resumo_folha.proventos (somado por competência)"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="% integrado"
          value={pct(kpis?.pct_integrado_geral)}
          icon={ArrowsLeftRight}
          iconColor="#1AA6B7"
          iconBg="#E2F4F6"
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Percentual de notas fiscais que estão integradas (com lançamento contábil) entre fiscal e contábil na carteira."
              dados="extract_integracao_fiscal_contabil — % das notas com valor > 0 sobre o total (por data_nota)"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Divergências"
          value={qtd(kpis?.total_divergencias ?? 0)}
          sub={`${qtd(kpis?.notas_sem_integracao ?? 0)} notas s/ integração`}
          icon={WarningCircle}
          iconColor={UI.neg}
          iconBg={UI.negSoft}
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Total de divergências apontadas pelo motor de auditoria em toda a carteira. O subtítulo mostra as notas sem integração fiscal-contábil."
              dados="Motor de auditoria (status = 'DIVERGENCIA') + extract_integracao_fiscal_contabil (valor = 0, por data_nota)"
              filtro={FILTRO.carteira}
            />
          }
        />
      </Grid>

      {/* Cobertura + classificação */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mb={5}>
        <Glass
          title="Cobertura por departamento"
          sub={`${qtd(kpis?.total_notas_manuais ?? 0)} notas manuais na carteira`}
        >
          <Box px={5} pb={5}>
            <CoberturaBar label="Fiscal" valor={num(kpis?.empresas_com_fiscal)} total={total} />
            <CoberturaBar label="Folha" valor={num(kpis?.empresas_com_folha)} total={total} />
            <CoberturaBar label="Contábil" valor={num(kpis?.empresas_com_contabil)} total={total} />
          </Box>
        </Glass>

        <Glass title="Empresas por classificação" sub={`${qtd(empresas?.length ?? 0)} no ranking`}>
          <Box px={5} pb={5}>
            {classificacoes.length === 0 && (
              <Text fontSize="13px" color={UI.muted}>
                Sem dados no período.
              </Text>
            )}
            {classificacoes.map(([nome, n]) => (
              <Flex key={nome} align="center" gap={3} mb={2.5}>
                <Text
                  w="84px"
                  fontSize="12px"
                  fontWeight={700}
                  color={UI.ink}
                  flexShrink={0}
                  noOfLines={1}
                >
                  {nome}
                </Text>
                <Box
                  flex={1}
                  h="9px"
                  borderRadius="full"
                  bg="rgba(15,23,42,0.06)"
                  overflow="hidden"
                >
                  <Box h="100%" borderRadius="full" bg={UI.brand} w={`${(n / maxClass) * 100}%`} />
                </Box>
                <Text
                  w="32px"
                  textAlign="right"
                  fontSize="13px"
                  fontWeight={800}
                  color={UI.ink}
                  flexShrink={0}
                >
                  {n}
                </Text>
              </Flex>
            ))}
          </Box>
        </Glass>
      </SimpleGrid>

      {/* Ranking de empresas */}
      <Box mb={5}>
        <Glass
          title="Ranking de empresas por risco"
          sub={buscaEmp ? `${empFiltradas.length} de ${empresas?.length ?? 0}` : undefined}
          action={
            <InputGroup size="sm" w={{ base: '100%', md: '260px' }}>
              <InputLeftElement pointerEvents="none" color={UI.muted}>
                <MagnifyingGlass size={15} />
              </InputLeftElement>
              <Input
                placeholder="Nome, CNPJ ou ID…"
                value={buscaEmp}
                onChange={e => setBuscaEmp(e.target.value)}
                borderRadius="10px"
                bg="white"
              />
            </InputGroup>
          }
        >
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg={UI.rowHover}>
                <Tr>
                  <Th>Empresa</Th>
                  <Th>CNPJ</Th>
                  <Th isNumeric>Verificações</Th>
                  <Th isNumeric>Divergências</Th>
                  <Th isNumeric>Alertas</Th>
                  <Th>Score</Th>
                  <Th>Classificação</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {empFiltradas.map(e => {
                  const score = num(e.score);
                  const cor = riscoColor(score);
                  return (
                    <Tr
                      key={e.id}
                      cursor="pointer"
                      _hover={{ bg: UI.rowHover }}
                      onClick={() => onOpenEmpresa(e.id)}
                      title="Abrir o dashboard desta empresa"
                    >
                      <Td maxW="240px" whiteSpace="normal">
                        <Text fontWeight={700} fontSize="12.5px">
                          {e.nome}
                        </Text>
                        <Text fontSize="11px" color={UI.muted}>
                          ID {e.external_id}
                        </Text>
                      </Td>
                      <Td>{e.cnpj}</Td>
                      <Td isNumeric>{qtd(e.total)}</Td>
                      <Td
                        isNumeric
                        color={num(e.divergencias) ? UI.neg : UI.muted}
                        fontWeight={700}
                      >
                        {qtd(e.divergencias)}
                      </Td>
                      <Td isNumeric color={num(e.alertas) ? UI.warn : UI.muted} fontWeight={700}>
                        {qtd(e.alertas)}
                      </Td>
                      <Td>
                        <Flex align="center" gap={2}>
                          <Box
                            w="44px"
                            h="6px"
                            borderRadius="full"
                            bg="rgba(15,23,42,0.08)"
                            overflow="hidden"
                          >
                            <Box
                              h="100%"
                              borderRadius="full"
                              bg={cor}
                              w={`${Math.min(score, 100)}%`}
                            />
                          </Box>
                          <Text fontWeight={800} fontSize="12.5px" color={cor}>
                            {score}
                          </Text>
                        </Flex>
                      </Td>
                      <Td>
                        <Badge
                          style={{ background: `${cor}1A`, color: cor }}
                          borderRadius="full"
                          px={2.5}
                          py={0.5}
                          fontSize="11px"
                          fontWeight={700}
                          textTransform="none"
                        >
                          {e.classificacao}
                        </Badge>
                      </Td>
                      <Td>
                        <IconButton
                          aria-label="Abrir Dossiê 360"
                          title="Abrir Dossiê 360"
                          icon={<Files size={16} weight="fill" />}
                          size="xs"
                          variant="ghost"
                          color={UI.brand}
                          onClick={ev => {
                            ev.stopPropagation();
                            onOpenEmpresaScreen(e.id, 'dossie');
                          }}
                        />
                      </Td>
                    </Tr>
                  );
                })}
                {empFiltradas.length === 0 && (
                  <Tr>
                    <Td colSpan={8} textAlign="center" py={6} color={UI.muted}>
                      {buscaEmp ? 'Nenhuma empresa com esse filtro.' : 'Sem dados no período.'}
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Glass>
      </Box>

      {/* Ranking de colaboradores */}
      <Glass
        title="Ranking de colaboradores"
        sub={buscaCol ? `${colFiltrados.length} de ${colaboradores?.length ?? 0}` : undefined}
        action={
          <InputGroup size="sm" w={{ base: '100%', md: '220px' }}>
            <InputLeftElement pointerEvents="none" color={UI.muted}>
              <MagnifyingGlass size={15} />
            </InputLeftElement>
            <Input
              placeholder="Colaborador…"
              value={buscaCol}
              onChange={e => setBuscaCol(e.target.value)}
              borderRadius="10px"
              bg="white"
            />
          </InputGroup>
        }
      >
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={UI.rowHover}>
              <Tr>
                <Th>Colaborador</Th>
                <Th isNumeric>Empresas</Th>
                <Th isNumeric>Verificações</Th>
                <Th isNumeric>Divergências</Th>
                <Th isNumeric>Alertas</Th>
                <Th>Score</Th>
              </Tr>
            </Thead>
            <Tbody>
              {colFiltrados.map(c => {
                const score = num(c.score);
                const cor = riscoColor(score);
                return (
                  <Tr key={c.usuario} _hover={{ bg: UI.rowHover }}>
                    <Td fontWeight={700}>
                      <Flex align="center" gap={2}>
                        <Box color={UI.muted}>
                          <Note size={14} />
                        </Box>
                        {c.usuario}
                      </Flex>
                    </Td>
                    <Td isNumeric>{qtd(c.empresas)}</Td>
                    <Td isNumeric>{qtd(c.total_checks)}</Td>
                    <Td isNumeric color={num(c.divergencias) ? UI.neg : UI.muted} fontWeight={700}>
                      {qtd(c.divergencias)}
                    </Td>
                    <Td isNumeric color={num(c.alertas) ? UI.warn : UI.muted} fontWeight={700}>
                      {qtd(c.alertas)}
                    </Td>
                    <Td>
                      <Flex align="center" gap={2}>
                        <Box
                          w="44px"
                          h="6px"
                          borderRadius="full"
                          bg="rgba(15,23,42,0.08)"
                          overflow="hidden"
                        >
                          <Box
                            h="100%"
                            borderRadius="full"
                            bg={cor}
                            w={`${Math.min(score, 100)}%`}
                          />
                        </Box>
                        <Text fontWeight={800} fontSize="12.5px" color={cor}>
                          {score}
                        </Text>
                      </Flex>
                    </Td>
                  </Tr>
                );
              })}
              {colFiltrados.length === 0 && (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={6} color={UI.muted}>
                    {buscaCol ? 'Nenhum colaborador com esse filtro.' : 'Sem dados no período.'}
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Glass>
    </Box>
  );
}
